import tw from 'twin.macro'
import { useCallback } from 'react'
import { graphql } from 'gatsby'
import RichText from '@/components/ui/rich-text'
import DynamicAction, { DynamicActionProps } from '@/components/ui/dynamic-action'
import Link from '@/components/link-component'
import colors from '@/constants/colors'
import HighlightTerms from './highlight-terms'

export type PricingCardProps = {
  headline: string
  subHeading?: any
  backgroundColor: string
  currency: string
  pricePretext: string
  price: number
  period: string
  periodDetail: string
  pricingSubtext?: string
  features?: string[]
  bottomText?: string
  actions?: DynamicActionProps[]
  hasShadow?: boolean
  actionsPosition?: string
  setCardAsLink?: boolean
  highlightTerms?: Array<{ term: string; definition: string }>
  columnHeading?: string
}

export const fragment = graphql`
  fragment ComponentPricing on ContentfulComponentPricing {
    id
    __typename
    headline
    subHeading {
      raw
      #  references {
      #    ...Link
      #  }
    }
    backgroundColor
    currency
    pricePretext
    price
    period
    periodDetail
    pricingSubtext: subtext
    features
    bottomText
    actions {
      ... on ContentfulComponentCtaLink {
        ...CtaLink
      }
      ... on ContentfulComponentHubspotCta {
        ...HubspotCTA
      }
    }
    actionsPosition
    setCardAsLink
    columnHeading
    columnHeadingColumnSpan
  }
`

export default function PricingCard({
  headline,
  subHeading,
  backgroundColor = 'White',
  actionsPosition = 'Bottom',
  currency,
  pricePretext,
  price,
  period,
  periodDetail,
  pricingSubtext,
  features,
  bottomText,
  actions = [],
  hasShadow = false,
  setCardAsLink = false,
  highlightTerms,
  columnHeading,
}: PricingCardProps): JSX.Element {
  const { indigoBlue, white } = colors

  const cardWrapCss = [
    tw`mx-auto mb-4 px-8 pt-8 pb-6 max-w-2xl rounded-4xl text-center flex flex-col justify-between w-full md:(px-12 pt-12 pb-8)`,
    backgroundColor === white && tw`bg-white`,
    backgroundColor === indigoBlue && tw`bg-indigoBlue`,
    hasShadow && tw`shadow-2xl`,
  ]

  const LinkWrap = useCallback(
    ({ children, ctas }) => (
      <>
        {columnHeading && <div tw="xl:hidden text-xl text-center py-2 text-white">{columnHeading}</div>}
        {setCardAsLink && ctas && ctas.length === 1 && ctas[0].link ? (
          <Link
            className="card-link-hover-style group"
            css={cardWrapCss}
            to={ctas[0].link.url}
            aria-label={`go to ${ctas[0].link.url}`}
          >
            {children}
          </Link>
        ) : (
          <div css={cardWrapCss}>{children}</div>
        )}
      </>
    ),
    []
  )

  const renderActions = () => (
    <div tw="flex flex-col gap-2" css={actions.length === 1 && tw`pb-9`}>
      {actions.map((action) => (
        <DynamicAction
          asButton={setCardAsLink}
          key={action.id}
          {...action}
          align="Center"
          backgroundColor={backgroundColor}
        />
      ))}
    </div>
  )
  return (
    <LinkWrap ctas={actions}>
      <>
        <div css={[backgroundColor === indigoBlue ? tw`text-white` : tw`text-indigoBlue`]}>
          <h4 tw="h5" data-testid="pricing-headline" css={[subHeading ? tw`mb-2` : tw`mb-12`]}>
            {headline}
          </h4>
          {subHeading && (
            <div tw="pb-6 [p]:leading-tight">
              <RichText body={subHeading} />
            </div>
          )}
          {currency && (
            <p tw="mb-4" css={[backgroundColor === white && tw`text-primaryBlue`]} data-testid="pricing-price">
              <span>{pricePretext}</span>
              <sup tw="text-[1.5rem] md:(text-[2.25rem]) top-[-1.5rem]" data-testid="pricing-currency">
                {currency}
              </sup>
              <span tw="text-[2.75rem] leading-10 md:text-[4rem]">{price}</span>
            </p>
          )}
          <span
            css={[backgroundColor === white && tw`text-gray-600`, backgroundColor === indigoBlue && tw`text-tealBlue`]}
          >
            {period && (
              <p tw="text-sm" css={periodDetail ? tw`mb-0` : tw`mb-6`} data-testid="pricing-period">
                {period}
              </p>
            )}
            {periodDetail && (
              <p tw="text-base mb-6" data-testid="pricing-periodDetail">
                {periodDetail}
              </p>
            )}
          </span>
          {actions && actionsPosition === 'Middle' && <div tw="mb-6">{renderActions()}</div>}
          {pricingSubtext && (
            <p tw="mb-6" data-testid="pricing-subtext">
              {pricingSubtext}
            </p>
          )}

          {features && (
            <div tw="flex justify-center">
              <ul tw="list-none p-0 lg:pl-4 mb-6 w-auto text-left" data-testid="pricing-features">
                {features.map((feature, index) => (
                  <li tw="relative mb-3 pl-3" key={`feature-${index}`}>
                    <span
                      tw="font-bwi absolute top-0 -left-3"
                      css={[
                        backgroundColor === white && tw`text-primaryBlue`,
                        backgroundColor === indigoBlue && tw`text-tealBlue`,
                      ]}
                    >
                      &#xe93a;
                    </span>
                    <HighlightTerms text={feature} terms={highlightTerms} />
                  </li>
                ))}
              </ul>
            </div>
          )}
          {bottomText && (
            <p tw="text-sm text-gray-600 m-0" data-testid="pricing-bottom-text">
              <HighlightTerms text={bottomText} terms={highlightTerms} />
            </p>
          )}
        </div>
        {actions && (actionsPosition === 'Bottom' || actionsPosition === null) && (
          <div tw="pt-6">{renderActions()}</div>
        )}
      </>
    </LinkWrap>
  )
}
